import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  CalendarClock,
  CheckCircle2,
  Clock3,
  Container,
  MapPinned,
  Navigation,
  Play,
  Route,
  Send,
  TimerOff,
  Truck,
  X,
} from "lucide-react";
import { listTrips, updateTrip } from "../api";
import type { AuthUser, Trip, Vehicle } from "../types";

const statusLabel = {
  planned: "PROGRAMADO",
  dispatched: "DESPACHADO",
  in_transit: "EN TRÁNSITO",
  delayed: "DEMORADO",
  completed: "COMPLETADO",
  cancelled: "CANCELADO",
};

type TripFilter = "active" | "planned" | "delayed" | "completed";

export function TripControlPanel({
  open,
  onClose,
  vehicles,
  user,
  onSelectVehicle,
}: {
  open: boolean;
  onClose: () => void;
  vehicles: Vehicle[];
  user: AuthUser;
  onSelectVehicle: (vehicle: Vehicle) => void;
}) {
  const [rows, setRows] = useState<Trip[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [filter, setFilter] = useState<TripFilter>("active");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const refresh = () => {
      listTrips()
        .then((items) => {
          setRows(items);
          setSelectedId((current) =>
            items.some((item) => item.id === current) ? current : items[0]?.id ?? null,
          );
          setError("");
        })
        .catch((cause) =>
          setError(cause instanceof Error ? cause.message : "No fue posible cargar los viajes"),
        );
    };
    refresh();
    window.addEventListener("gsf:trip-updated", refresh);
    return () => window.removeEventListener("gsf:trip-updated", refresh);
  }, [open]);

  const visibleRows = useMemo(
    () =>
      rows.filter((trip) => {
        if (filter === "active") return ["dispatched", "in_transit", "delayed"].includes(trip.status);
        if (filter === "planned") return trip.status === "planned";
        if (filter === "delayed") return trip.status === "delayed";
        return ["completed", "cancelled"].includes(trip.status);
      }),
    [filter, rows],
  );
  const selected =
    visibleRows.find((row) => row.id === selectedId) || visibleRows[0] || rows[0];
  const selectedVehicle = vehicles.find((vehicle) => vehicle.id === selected?.vehicle_id);
  const canDispatch = ["superadmin", "fleet_admin", "operator", "dispatcher"].includes(user.role);
  const summary = useMemo(
    () => ({
      active: rows.filter((row) => ["dispatched", "in_transit", "delayed"].includes(row.status)).length,
      planned: rows.filter((row) => row.status === "planned").length,
      delayed: rows.filter((row) => row.status === "delayed").length,
    }),
    [rows],
  );

  async function transition(status: Trip["status"], note: string) {
    if (!selected) return;
    setPending(true);
    setError("");
    try {
      const updated = await updateTrip(selected.id, status, note);
      setRows((current) => current.map((row) => row.id === updated.id ? updated : row));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No fue posible actualizar el viaje");
    } finally {
      setPending(false);
    }
  }

  if (!open) return null;
  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside className="admin-panel trip-panel" role="dialog" aria-modal="true" aria-labelledby="trip-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div>
            <span className="eyebrow">TORRE DE CONTROL · VIAJES Y DESPACHO</span>
            <h2 id="trip-title">Operación nacional</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar viajes"><X size={18} /></button>
        </header>
        <section className="admin-panel__summary">
          <span><Navigation size={17} /><b>{summary.active}</b> activos</span>
          <span><CalendarClock size={17} /><b>{summary.planned}</b> programados</span>
          <span><TimerOff size={17} /><b>{summary.delayed}</b> demorados</span>
        </section>
        <div className="trip-panel__body">
          <section className="trip-queue">
            <div className="trip-filters">
              {(["active", "planned", "delayed", "completed"] as TripFilter[]).map((item) => (
                <button type="button" className={filter === item ? "is-active" : ""} onClick={() => setFilter(item)} key={item}>
                  {{ active: "ACTIVOS", planned: "PROGRAMADOS", delayed: "DEMORADOS", completed: "HISTÓRICO" }[item]}
                </button>
              ))}
            </div>
            <div className="trip-list">
              {visibleRows.map((trip) => (
                <button
                  type="button"
                  className={`trip-item trip-item--${trip.status}${selected?.id === trip.id ? " is-selected" : ""}`}
                  onClick={() => setSelectedId(trip.id)}
                  key={trip.id}
                >
                  <Truck size={16} />
                  <span>
                    <small>{trip.trip_number} · {trip.vehicle_unit_code}</small>
                    <strong>{trip.origin_name} → {trip.destination_name}</strong>
                    <em>{trip.driver_name} · {trip.trailer_unit_code || "Sin remolque"}</em>
                  </span>
                  <b>{statusLabel[trip.status]}</b>
                  <i><em style={{ width: `${trip.progress_percent}%` }} /></i>
                </button>
              ))}
              {!visibleRows.length && <p className="maintenance-empty">SIN VIAJES EN ESTA VISTA</p>}
            </div>
          </section>
          <section className="trip-detail">
            {selected && (
              <>
                <div className={`trip-progress trip-progress--${selected.status}`} style={{ "--trip-progress": `${selected.progress_percent * 3.6}deg` } as React.CSSProperties}>
                  <span><b>{Math.round(selected.progress_percent)}%</b><small>AVANCE</small></span>
                </div>
                <div className="trip-detail__title">
                  <span className="eyebrow">{selected.trip_number} · {statusLabel[selected.status]}</span>
                  <h3>{selected.origin_name} → {selected.destination_name}</h3>
                  <p>{selected.customer_name} · {selected.vehicle_unit_code}</p>
                </div>
                <div className="trip-kpis">
                  <article><Clock3 size={16} /><span><small>ETA OPERATIVA</small><strong>{selected.eta_at ? new Date(selected.eta_at).toLocaleString("es-MX") : "Pendiente"}</strong></span></article>
                  <article className={selected.delay_minutes ? "is-delay" : ""}><TimerOff size={16} /><span><small>DEMORA ESTIMADA</small><strong>{selected.delay_minutes} min</strong></span></article>
                </div>
                <div className="trip-manifest">
                  <article><Container size={16} /><span><small>CARGA</small><strong>{selected.cargo_description}</strong></span></article>
                  <article><Route size={16} /><span><small>CORREDOR</small><strong>{selected.route_name || "Sin corredor"}</strong></span></article>
                  <article><MapPinned size={16} /><span><small>TRACTO / REMOLQUE</small><strong>{selected.vehicle_unit_code} / {selected.trailer_unit_code || "N/A"}</strong></span></article>
                </div>
                <div className="trip-events">
                  <span className="eyebrow">BITÁCORA DE VIAJE</span>
                  {selected.events.map((event) => (
                    <article key={event.id}><Navigation size={13} /><span><strong>{event.note}</strong><small>{event.event_type} · {new Date(event.captured_at).toLocaleString("es-MX")}</small></span></article>
                  ))}
                </div>
                {canDispatch && !["completed", "cancelled"].includes(selected.status) && (
                  <div className="trip-actions">
                    {selected.status === "planned" && <button type="button" disabled={pending} onClick={() => transition("dispatched", "Viaje liberado por despacho C5.")}><Send size={13} /> DESPACHAR</button>}
                    {selected.status === "dispatched" && <button type="button" disabled={pending} onClick={() => transition("in_transit", "Salida confirmada por torre de control.")}><Play size={13} /> CONFIRMAR SALIDA</button>}
                    {selected.status === "delayed" && <button type="button" disabled={pending} onClick={() => transition("in_transit", "Demora validada; operación normalizada.")}><Navigation size={13} /> NORMALIZAR</button>}
                    {["in_transit", "delayed"].includes(selected.status) && <button type="button" disabled={pending} onClick={() => transition("completed", "Entrega y arribo confirmados por despacho.")}><CheckCircle2 size={13} /> CONFIRMAR ENTREGA</button>}
                  </div>
                )}
                {selectedVehicle && (
                  <button type="button" className="primary-button" onClick={() => { onSelectVehicle(selectedVehicle); onClose(); }}>
                    ABRIR EN MAPA <ArrowRight size={14} />
                  </button>
                )}
              </>
            )}
          </section>
        </div>
        {error && <div className="admin-message admin-message--error">{error}</div>}
      </aside>
    </div>
  );
}
